import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, GraduationCap, Pencil, FileText, Users, Sparkles } from "lucide-react";
import { LESSON_TYPE_LABEL } from "@/constants/options";
import AvailabilityGrid from "@/components/AvailabilityGrid";
import ImageLightbox from "@/components/ImageLightbox";

export default function TeacherOwnProfile() {
  const [profile, setProfile] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/profile");
        setProfile(data.profile);
      } catch {}
    })();
  }, []);

  if (!profile) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display text-3xl font-semibold">Profilin henüz oluşturulmadı</h1>
          <p className="mt-3 text-neutral-500">Ailelere görünmek için profilini tamamla.</p>
          <Link to="/ogretmen/kurulum"><Button className="mt-6 rounded-full bg-neutral-900 hover:bg-neutral-800">Profili Oluştur</Button></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex items-start justify-between reveal">
          <div>
            <span className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Öğretmen paneli</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter">Profilim</h1>
          </div>
          <Link to="/ogretmen/kurulum">
            <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn"><Pencil size={14} className="mr-2" /> Düzenle</Button>
          </Link>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200">
              {profile.photo_path ? (
                <img
                  src={fileUrl(profile.photo_path)}
                  alt={profile.name}
                  onClick={() => setLightbox({ src: fileUrl(profile.photo_path), alt: profile.name })}
                  className="w-full h-full object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  data-testid="own-photo-clickable"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-neutral-400"><GraduationCap size={48} /></div>
              )}
            </div>
            {profile.yks_doc_path && (
              <button
                type="button"
                onClick={() => setLightbox({ src: fileUrl(profile.yks_doc_path), alt: "YKS Sonuç Belgesi" })}
                className="mt-4 w-full border border-slate-200 rounded-lg overflow-hidden hover:border-[color:var(--brand)] transition-colors group"
                data-testid="own-yks-clickable"
              >
                <div className="aspect-[4/3] bg-slate-50">
                  <img src={fileUrl(profile.yks_doc_path)} alt="YKS Sonuç Belgesi" className="w-full h-full object-cover cursor-zoom-in group-hover:opacity-95 transition-opacity" />
                </div>
                <div className="p-3 flex items-center gap-2 text-sm text-slate-700">
                  <FileText size={16} className="text-[color:var(--brand)]" /> YKS Sonuç Belgesi
                </div>
              </button>
            )}
          </div>

          <div className="md:col-span-2">
            {user?.verified ? (
              <span className="inline-flex items-center gap-1 bg-[color:var(--brand)] text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm shadow-blue-500/30">
                <ShieldCheck size={13} fill="white" /> Doğrulanmış Öğretmen
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 border border-slate-200 text-slate-500 rounded-full px-3 py-1 text-xs font-medium">
                <ShieldCheck size={13} /> Doğrulanma bekliyor
              </span>
            )}
            <h2 className="mt-4 font-display text-3xl font-semibold">{profile.name}</h2>
            <p className="mt-2 text-neutral-600">{profile.university} · {profile.age} yaşında</p>
            {profile.department && (
              <p className="mt-1 text-sm text-[color:var(--brand)] font-medium" data-testid="own-department">
                Bölüm: {profile.department}
              </p>
            )}

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat icon={<Users size={16} />} label="Aktif öğrenci" value={profile.active_students} />
              <Stat icon={<Sparkles size={16} />} label="Müsait kontenjan" value={profile.available_slots} />
              <Stat icon={<GraduationCap size={16} />} label="Branş sayısı" value={profile.subjects?.length || 0} />
            </div>

            <Section title="Branşlar"><TagList tags={profile.subjects} /></Section>
            <Section title="Sınıf seviyeleri"><TagList tags={profile.class_levels} /></Section>
            <Section title="Ders türü"><TagList tags={(profile.lesson_types || []).map((v) => LESSON_TYPE_LABEL[v] || v)} /></Section>
            <Section title="Hakkında"><p className="text-neutral-700 leading-relaxed whitespace-pre-line">{profile.intro}</p></Section>
          </div>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-12">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-2xl font-semibold tracking-tight">Müsaitlik Takvimi</h3>
            <span className="text-xs text-slate-500">Ders verebileceğin saatleri işaretle</span>
          </div>
          {profile.user_id && <AvailabilityGrid teacherId={profile.user_id} editable={true} />}
        </div>

        {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
      </div>
    </Layout>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border border-neutral-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium flex items-center gap-1.5">{icon} {label}</div>
      <div className="mt-2 font-display text-2xl font-semibold">{value}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="mt-8">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium mb-3">{title}</div>
      {children}
    </div>
  );
}
function TagList({ tags }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(tags || []).map((t) => <span key={t} className="text-sm px-3 py-1.5 rounded-full border border-neutral-200 bg-neutral-50">{t}</span>)}
    </div>
  );
}
